import { t } from "i18next";
import { CHART_ACCENT } from "@constants/resultColors";
import { EyeType, PupillometryData } from "types/testTypes";
import { VerticalReferenceLine } from "types/uiTypes";

type PupilData = {
  timestamp: number;
  pupilSize: number;
  targetEye: "Left" | "Right" | "Both";
  stimulusActive?: boolean;
};

type PupillometryMetricValue = number | null;

const MIN_MEASURABLE_CHANGE_PERCENT = 3;

const sortByTimestamp = (data: PupilData[]) =>
  [...data].sort((left, right) => left.timestamp - right.timestamp);

const getStimulusOnsetIndex = (data: PupilData[]) => {
  const onsetIndex = data.findIndex((sample) => sample.stimulusActive === true);
  return onsetIndex >= 0 ? onsetIndex : 0;
};

const average = (values: number[]) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

const getSegmentStats = (data: PupilData[]) => {
  if (!data.length) {
    return null;
  }

  const sortedData = sortByTimestamp(data);
  const onsetIndex = getStimulusOnsetIndex(sortedData);
  const onsetSample = sortedData[onsetIndex] ?? sortedData[0];
  const baselineSamples = onsetIndex > 0 ? sortedData.slice(0, onsetIndex) : sortedData.slice(0, 1);
  const postStimulusSamples = sortedData.slice(onsetIndex);

  if (!onsetSample || !postStimulusSamples.length) {
    return null;
  }

  const baseline = average(baselineSamples.map((sample) => sample.pupilSize));
  if (baseline === null || baseline <= 0) {
    return null;
  }

  const minPoint = postStimulusSamples.reduce((min, current) =>
    current.pupilSize < min.pupilSize ? current : min,
  );

  return {
    sortedData,
    onsetSample,
    baseline,
    postStimulusSamples,
    minPoint,
  };
};

const calculateMinPupilSize = (data: PupilData[]): PupillometryMetricValue => {
  const stats = getSegmentStats(data);
  return stats?.minPoint.pupilSize ?? null;
};

const calculateInstantaneousCV = (data: PupilData[]): number[] =>
  data
    .slice(1)
    .map((d, i) => {
      const deltaTime = d.timestamp - data[i].timestamp;
      if (deltaTime <= 0) return null;
      const velocity = (d.pupilSize - data[i].pupilSize) / deltaTime;
      return Number.isFinite(velocity) ? velocity : null;
    })
    .filter((velocity): velocity is number => velocity !== null);

const calculateMCV = (data: PupilData[]): PupillometryMetricValue => {
  const postStimulusSamples = getSegmentStats(data)?.postStimulusSamples ?? [];
  const velocities = calculateInstantaneousCV(postStimulusSamples);
  if (!velocities.length) return null;
  return Math.min(...velocities);
};

const calculateSingleCV = (data: PupilData[]): PupillometryMetricValue => {
  const stats = getSegmentStats(data);
  if (!stats) return null;

  const constrictionOnset = calculateLatencySample(data, stats.baseline) ?? stats.onsetSample;
  const deltaTime = stats.minPoint.timestamp - constrictionOnset.timestamp;
  if (deltaTime <= 0) return null;

  const velocity = Math.abs((stats.minPoint.pupilSize - stats.baseline) / deltaTime);
  return Number.isFinite(velocity) ? velocity : null;
};

const calculateChangeInPupilSize = (data: PupilData[]): PupillometryMetricValue => {
  const stats = getSegmentStats(data);
  if (!stats) return null;

  return ((stats.baseline - stats.minPoint.pupilSize) / stats.baseline) * 100;
};

const calculateLatencySample = (
  data: PupilData[],
  baseline: number,
  threshold: number = 0.1,
): PupilData | null => {
  const stats = getSegmentStats(data);
  if (!stats) return null;

  const target = baseline - threshold;
  return stats.postStimulusSamples.find((sample) => sample.pupilSize <= target) ?? null;
};

const calculateLatency = (data: PupilData[], threshold: number = 0.1): PupillometryMetricValue => {
  const stats = getSegmentStats(data);
  if (!stats) return null;

  const latencySample = calculateLatencySample(data, stats.baseline, threshold);
  if (!latencySample) return null;

  return latencySample.timestamp - stats.onsetSample.timestamp;
};

const calculateDilationVelocity = (data: PupilData[]): PupillometryMetricValue => {
  const stats = getSegmentStats(data);
  if (!stats) return null;

  const minIndex = stats.postStimulusSamples.findIndex(
    (sample) => sample.timestamp === stats.minPoint.timestamp,
  );
  if (minIndex < 0 || minIndex >= stats.postStimulusSamples.length - 1) return null;

  const dilationData = stats.postStimulusSamples.slice(minIndex);

  const dilationVelocities = dilationData
    .slice(1)
    .map((d, i) => {
      const deltaTime = d.timestamp - dilationData[i].timestamp;
      if (deltaTime <= 0) return null;
      const velocity = (d.pupilSize - dilationData[i].pupilSize) / deltaTime;
      return Number.isFinite(velocity) ? velocity : null;
    })
    .filter((velocity): velocity is number => velocity !== null);

  if (!dilationVelocities.length) return null;

  return Math.max(...dilationVelocities);
};

export const calculatePupillometryResults = (
  data: PupilData[],
): { label: string; value: PupillometryMetricValue; unit: string }[] => {
  if (data.length === 0) return [];

  const filtered = data.filter((d) => d.pupilSize !== null);
  const baseline = getSegmentStats(filtered)?.baseline ?? filtered[0]?.pupilSize ?? null;

  const min = calculateMinPupilSize(filtered);
  const mcvRaw = calculateMCV(filtered);
  const mcv = typeof mcvRaw === "number" ? Math.abs(mcvRaw) : null;
  const singleCVRaw = calculateSingleCV(filtered);
  const singleCV = typeof singleCVRaw === "number" ? singleCVRaw : null;
  const changeInPupilSize = calculateChangeInPupilSize(filtered);
  const dynamicMetricsAreReliable =
    typeof changeInPupilSize === "number" && changeInPupilSize >= MIN_MEASURABLE_CHANGE_PERCENT;
  const latency = dynamicMetricsAreReliable ? calculateLatency(filtered) : null;
  const dilationVelocity = dynamicMetricsAreReliable ? calculateDilationVelocity(filtered) : null;

  return [
    { label: "size", value: baseline, unit: "mm" },
    { label: "min", value: min, unit: "mm" },
    { label: "cv", value: dynamicMetricsAreReliable ? singleCV : null, unit: "mm/s" },
    { label: "mcv", value: dynamicMetricsAreReliable ? mcv : null, unit: "mm/s" },
    { label: "ch", value: changeInPupilSize, unit: "%" },
    { label: "lat", value: latency, unit: "sec" },
    { label: "dv", value: dilationVelocity, unit: "mm/s" },
  ];
};

export function getReferenceLinesForPupillometryDataset(
  pupillometryData: PupillometryData[],
): VerticalReferenceLine[] {
  const referenceLines: VerticalReferenceLine[] = [];
  let isLastStimulusActive = false;
  for (const data of [...pupillometryData].sort(
    (left, right) => left.timestamp - right.timestamp,
  )) {
    if (data.stimulusActive) {
      if (isLastStimulusActive) {
        continue;
      }
      isLastStimulusActive = true;
      referenceLines.push({
        x: data.timestamp,
        label: t("stimulusActive"),
        color: CHART_ACCENT.stimulus,
      });
    } else {
      isLastStimulusActive = false;
    }
  }

  if (referenceLines.length > 1) {
    referenceLines.forEach((line) => {
      line.label = "S";
    });
  }

  return referenceLines;
}

export function getEyeDataForPupillometry(
  pupillometryData: PupillometryData[],
  eyeType: EyeType.LeftEye | EyeType.RightEye,
  approach: "DIRECT" | "CONSENTUAL" | "NORMAL",
) {
  const sortedData = [...pupillometryData].sort((left, right) => left.timestamp - right.timestamp);
  const baseline = sortedData[0]?.timestamp ?? 0;
  let filteredResults;

  switch (approach) {
    case "DIRECT":
      filteredResults = sortedData.filter(
        (result) =>
          ((result.targetEye === "Left" && eyeType === EyeType.LeftEye) ||
            (result.targetEye === "Right" && eyeType === EyeType.RightEye) ||
            result.targetEye === "Both") &&
          result.timestamp >= baseline,
      );
      break;
    case "CONSENTUAL":
      filteredResults = sortedData.filter(
        (result) =>
          ((result.targetEye === "Left" && eyeType === EyeType.RightEye) ||
            (result.targetEye === "Right" && eyeType === EyeType.LeftEye) ||
            result.targetEye === "Both") &&
          result.timestamp >= baseline,
      );
      break;
    case "NORMAL":
      filteredResults = sortedData.filter(
        (result) => result.targetEye === "Both" && result.timestamp >= baseline,
      );
      break;
    default:
      throw new Error("Invalid approach type: " + approach);
  }

  const dividedData = dividePupilDataByStimulus(filteredResults);
  const resultCollection = dividedData.map((results) => {
    return calculatePupillometryResults(
      results.map((result) => ({
        timestamp: result.timestamp,
        pupilSize: eyeType === EyeType.LeftEye ? result.leftPupil : result.rightPupil,
        targetEye: result.targetEye,
        stimulusActive: result.stimulusActive,
      })),
    );
  });
  return aggregateResultCollection(resultCollection);
}

const aggregateResultCollection = (
  resultCollection: any[],
): { label: string; value: number; unit: string }[] | null => {
  // Handle empty case
  if (!resultCollection || resultCollection.length === 0) {
    return null;
  }

  // Create object to store accumulated values
  const aggregatedData: { [key: string]: { values: number[]; unit: string } } = {};
  const aggregateModeByLabel: Record<string, "mean" | "median"> = {
    size: "mean",
    min: "mean",
    cv: "median",
    mcv: "median",
    ch: "median",
    lat: "median",
    dv: "median",
  };

  // Collect all property keys we encounter to preserve ordering
  const allKeys: string[] = [];

  // Accumulate values across all results
  for (const result of resultCollection) {
    if (!Array.isArray(result)) continue;

    for (const item of result) {
      if (!item || typeof item !== "object") continue;

      const { label, value, unit } = item;
      if (label === undefined || value === undefined || value === null || !Number.isFinite(value)) {
        continue;
      }

      // Track unique keys in order of first appearance
      if (!allKeys.includes(label)) {
        allKeys.push(label);
      }

      if (!aggregatedData[label]) {
        aggregatedData[label] = { values: [], unit: unit || "" };
      }

      aggregatedData[label].values.push(value);
    }
  }

  const getMedian = (values: number[]) => {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      return (sorted[mid - 1] + sorted[mid]) / 2;
    }

    return sorted[mid];
  };

  // Convert to array format and calculate means using the original order
  return allKeys
    .filter((key) => aggregatedData[key]?.values?.length)
    .map((key) => ({
      label: key,
      value:
        aggregateModeByLabel[key] === "median"
          ? getMedian(aggregatedData[key].values)
          : aggregatedData[key].values.reduce((sum, value) => sum + value, 0) /
            aggregatedData[key].values.length,
      unit: aggregatedData[key].unit,
    }));
};

const dividePupilDataByStimulus = (pupilData: PupillometryData[]): PupillometryData[][] => {
  // Divide the pupillometry data each time the stimulus changes from inactive to active
  // A "segment" consists of the following structure: inactive stimuli -> active stimuli -> inactive stimuli

  // When two segments follow each other
  // we should divide the last inactive stimuli group into two and distribute it to the two segments

  // e.g. [inactive1, active1, inactive2, active2, inactive3] ->
  // [inactive1, active1, inactive2(first half)], [inactive2(second half), active2, inactive3]

  // or when there is one segment
  // e.g. [inactive1, active1, inactive2] -> [inactive1, active1, inactive2] (no division needed)
  if (pupilData.length === 0) return [];

  const segments: PupillometryData[][] = [];
  let isActive = null;

  //First divide the data into segments by checking the stimulus active state
  const nonSortedData: PupillometryData[][] = [];
  let tempBatch: PupillometryData[] = [];
  for (const data of pupilData) {
    // If the tempBatch is empty, we can just add the data to it
    if (tempBatch.length === 0) {
      tempBatch.push(data);
      isActive = data.stimulusActive;
    } else if (data.stimulusActive) {
      // If the data is active, we check if the last data was also active
      // If it was, we just add the data to the tempBatch
      if (isActive === true) {
        tempBatch.push(data);
      } else if (isActive === false) {
        // If the last data was inactive, we push the tempBatch to the nonSortedData
        // and start a new tempBatch with the current data
        nonSortedData.push(tempBatch);
        tempBatch = [data];
      }
      isActive = true;
    } else if (data.stimulusActive === false) {
      if (isActive === false) {
        // If the last data was inactive, and current data is also inactive,
        // we just add the data to the tempBatch
        tempBatch.push(data);
      } else if (isActive === true) {
        // If the last data was active, we push the tempBatch to the nonSortedData
        // and start a new tempBatch with the current data
        nonSortedData.push(tempBatch);
        tempBatch = [data];
        isActive = false;
      }
    }
  }
  nonSortedData.push(tempBatch); // Push the last batch to nonSortedData

  // Now we have a nicely grouped data. Next we need to divide this data into segments as described above
  let segment: PupillometryData[] = [];
  for (let i = 0; i < nonSortedData.length; i++) {
    const batch = nonSortedData[i];
    if (batch.length === 0) continue;
    if (i === 0) {
      // First batch, (the first part where all data is inactive), just add it to the segment
      segment.push(...batch);
    } else if (i === nonSortedData.length - 1) {
      // Last batch, (the last part where all data is inactive), just add it to the segment
      // and push the segment to the segments array since it's complete
      segment.push(...batch);
      segments.push(segment);
    } else {
      // Middle batches, if the batch is active, we can just add it to the segment
      // If the batch is inactive, we need to divide it into two parts
      // if the segment is empty, we add the last half of the batch to the segment
      // if the segment is not empty, we add the first half of the batch to the because it means that the last segment is active
      const isBatchActive = batch[0].stimulusActive;
      if (isBatchActive) {
        // If the batch is active, just add it to the segment
        segment.push(...batch);
        continue;
      } else {
        const half = Math.ceil(batch.length / 2);
        const firstHalf = batch.slice(0, half);
        const secondHalf = batch.slice(half);
        if (segment.length === 0) {
          // If the segment is empty, we add the second half of the batch to the segment
          // (the first half of this batch should already be added to the previous segment)
          segment.push(...secondHalf);
        } else {
          segment.push(...firstHalf);
          segments.push(segment);
          segment = secondHalf;
        }
      }
    }
  }
  return segments;
};
